// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Checkbox, Chip, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField } from "@mui/material";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useLang from "@/hooks/useLang";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyMenuLayout, TyMenuMode } from "@/hooks/useStore/types";
import type { TyActiveTheme, TyApiResponse, TyMenuThemeOption } from "@/types";
import { getTextFieldSx } from "@/utils";
import MenuPreviewPopup from "@/UI/popups/MenuPreviewPopup";
import OutlinedField from "@/UI/components/OutlinedField";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type MenuThemePopupDetails = {
    theme: TyMenuThemeOption;
    /** What the store runs today. Its settings are carried over when the popup opens on the same
     *  theme, so re-opening the active theme does not silently reset what the owner already chose. */
    active: TyActiveTheme;
    onSuccess: (activeTheme: TyActiveTheme) => void;
};

type Props = {
    onClose: () => void;
    details?: MenuThemePopupDetails;
};

// --- Constants ----------------------------------------------------------------------------------

/** Mirrors `HEX_COLOR_REGEX` in the backend — keep the two in step or the form accepts what the API rejects. */
const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

/** Menuket's own red, which every theme falls back to while a store has picked no color of its own. */
const DEFAULT_MENU_COLOR = "#DD0D17";

/** How long to sit still after the last change before rebuilding the preview. Dragging a color picker
 *  fires continuously, and each rebuild re-renders the store's whole menu on the server. */
const PREVIEW_DEBOUNCE = 450;

/**
 * The frame is always rendered at one phone size and then scaled to fit, rather than resized. A menu
 * laid out for 320px is a different menu — the grid drops to narrower cards and the price rows trim
 * differently — so resizing the frame would preview something the owner's customers never see.
 */
const FRAME_WIDTH = 390;
const FRAME_HEIGHT = 550;

const chipSx = {
    backgroundColor: "var(--color-shaded-brand)",
    color: "var(--color-dark)",
    border: "1px solid var(--color-mid-brand)",
    height: "24px",
    fontFamily: "Alexandria, sans-serif",
    "& .MuiChip-label": { fontSize: "12px", fontWeight: 600, padding: "0 8px" },
};

const checkboxSx = { color: "var(--color-gray-01)", "&.Mui-checked": { color: "var(--color-brand)" } };

const listItemSx = { "& .MuiTypography-root": { fontFamily: "Alexandria, sans-serif", fontSize: "13.5px" } };

// --- Logic --------------------------------------------------------------------------------------

/**
 * One theme, set up before it is committed to. Everything here previews live and nothing reaches the
 * store's menu until "apply" — which is the whole point of settling a theme in a popup rather than
 * editing the live one from the gallery behind it.
 */
export default function MenuThemePopup({ onClose, details }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);
    const addComp = usePopup((state) => state.addComp);
    const dir = lang === "ar" ? "rtl" : "ltr";
    const textFieldSx = getTextFieldSx(dir);

    const theme = details!.theme;
    const active = details!.active;
    const isActive = active.theme === theme.key;

    // A theme the store already runs opens on its own saved settings; any other opens on everything
    // that theme offers, which is what a customer would get if it were applied untouched.
    const [modes, setModes] = useState<TyMenuMode[]>(() =>
        isActive && active.modes.length > 0 ? active.modes.filter((m) => theme.modes.includes(m)) : theme.modes
    );
    const [layouts, setLayouts] = useState<TyMenuLayout[]>(() =>
        isActive && active.layouts.length > 0 ? active.layouts.filter((l) => theme.layouts.includes(l)) : theme.layouts
    );
    const [color, setColor] = useState<string>(() => active.color ?? DEFAULT_MENU_COLOR);

    const isColorValid = HEX_COLOR_REGEX.test(color);

    /** Bumped once the inputs stop moving — see `PREVIEW_DEBOUNCE`. */
    const [previewKey, setPreviewKey] = useState(0);

    const query = useMemo(
        () => ({
            theme: theme.key,
            modes: modes.join(","),
            layouts: layouts.join(","),
            ...(theme.usesAccent && isColorValid ? { color } : {}),
        }),
        [theme.key, theme.usesAccent, modes, layouts, color, isColorValid]
    );

    const {
        data: previewData,
        loading: previewLoading,
        invoke: rebuildPreview,
    } = useFetch<TyApiResponse<{ html: string }>>("/menu/preview", { query }, false);

    const {
        data: saveData,
        loading: saving,
        error: saveError,
        invoke: save,
    } = useFetch<TyApiResponse<{ activeTheme: TyActiveTheme }>>("/settings/active-theme", { method: "PUT" }, false);

    useEffect(() => {
        const timer = setTimeout(() => setPreviewKey((prev) => prev + 1), PREVIEW_DEBOUNCE);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        rebuildPreview({ query });
    }, [previewKey]);

    useEffect(() => {
        if (!saveData?.success) return;
        details?.onSuccess(saveData.data.activeTheme);
        onClose();
    }, [saveData]);

    // --- Preview scaling -------------------------------------------------------------------------

    const stageRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    /** Never scales past 1 — a small laptop shrinks the phone, a large one does not blow it up into
     *  a preview of a device nobody holds. */
    useLayoutEffect(() => {
        const fit = () => {
            const stage = stageRef.current;
            if (!stage) return;
            const available = Math.min(stage.clientWidth / FRAME_WIDTH, stage.clientHeight / FRAME_HEIGHT);
            setScale(Math.min(1, available || 1));
        };
        fit();
        const observer = new ResizeObserver(fit);
        if (stageRef.current) observer.observe(stageRef.current);
        window.addEventListener("resize", fit);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", fit);
        };
    }, []);

    // --- Handlers --------------------------------------------------------------------------------

    /** The last remaining choice cannot be cleared — a menu with no appearance or no layout has
     *  nothing to render at all, which is the same rule the API enforces on the way in. Order is the
     *  owner's own: the menu opens on the first entry, so click order is kept rather than sorted. */
    const handlePick = <T extends string>(next: T[], offered: T[], set: (value: T[]) => void, current: T[]) => {
        const valid = next.filter((entry) => offered.includes(entry));
        set(valid.length > 0 ? valid : current);
    };

    const handleApply = () => {
        if (saving || (theme.usesAccent && !isColorValid)) return;
        save({
            method: "PUT",
            data: {
                theme: theme.key,
                modes,
                layouts,
                // A theme that paints nothing with the accent is sent none, so nothing is stored that
                // the menu would ignore and the next theme would inherit by accident.
                color: theme.usesAccent ? color.toUpperCase() : null,
            },
        });
    };

    const themeName = lang === "ar" ? theme.name.ar || theme.name.en : theme.name.en || theme.name.ar;
    const html = previewData?.data?.html;

    const handleExpand = () => {
        if (!html) return;
        addComp({
            name: "menu-theme-preview",
            Component: MenuPreviewPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            details: { html, title: themeName },
        });
    };

    return (
        <div className="menu-theme-popup">
            <div className="mtp-header">
                <div className="mtp-heading">
                    <p className="mtp-title">{themeName}</p>
                    <p className="mtp-subtitle">{t.menuTheme.popupSubtitle}</p>
                </div>
                {isActive && <span className="mtp-active-badge">{t.menuTheme.activeBadge}</span>}
                <button type="button" className="mtp-close" onClick={onClose} aria-label={t.menuTheme.cancelLabel}>
                    <img src="/icons/close-icon.svg" alt="" width={16} height={16} />
                </button>
            </div>

            <div className="mtp-body">
                <div className="mtp-preview">
                    <div className="mtp-stage" ref={stageRef}>
                        {/* Rendered at a fixed phone size and scaled down as one piece — see FRAME_WIDTH */}
                        <div className="mtp-phone" style={{ width: FRAME_WIDTH, height: FRAME_HEIGHT, transform: `scale(${scale})` }}>
                            {/* Sandboxed into its own document so the menu's own styles and script
                                behave exactly as they do live, the same way the full preview does. */}
                            {html && <iframe className="mtp-frame" title={t.menuTheme.previewTitle} srcDoc={html} />}
                            {(previewLoading || !html) && (
                                <div className="mtp-frame-note">
                                    <span className="mtp-spinner" />
                                    {t.menuTheme.previewLoading}
                                </div>
                            )}
                        </div>

                        {/* Floats over the frame rather than sitting under it, so the preview keeps
                            every pixel of the stage it was scaled to fit. */}
                        <button type="button" className="mtp-expand" onClick={handleExpand} disabled={!html} title={t.menuTheme.expandPreviewLabel}>
                            <img
                                src="/icons/eye-icon.svg"
                                alt=""
                                width={15}
                                height={15}
                                onError={(e) => ((e.currentTarget as HTMLImageElement).style.display = "none")}
                            />
                            {t.menuTheme.expandPreviewLabel}
                        </button>
                    </div>
                    <p className="mtp-preview-note">{t.menuTheme.previewNote}</p>
                </div>

                <div className="mtp-settings">
                    <div className="mtp-field">
                        <FormControl fullWidth disabled={saving} sx={textFieldSx}>
                            <InputLabel>{t.menuTheme.modesLabel}</InputLabel>
                            <Select
                                multiple
                                value={modes}
                                onChange={(e) => handlePick(e.target.value as TyMenuMode[], theme.modes, setModes, modes)}
                                input={<OutlinedInput label={t.menuTheme.modesLabel} />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {(selected as TyMenuMode[]).map((mode) => (
                                            <Chip
                                                key={mode}
                                                label={mode === "light" ? t.menuTheme.modeLight : t.menuTheme.modeDark}
                                                size="small"
                                                sx={chipSx}
                                            />
                                        ))}
                                    </Box>
                                )}
                                MenuProps={{ slotProps: { paper: { style: { maxHeight: 48 * 4.5 + 8 } } } }}
                            >
                                {theme.modes.map((mode) => (
                                    <MenuItem key={mode} value={mode}>
                                        <Checkbox checked={modes.includes(mode)} sx={checkboxSx} />
                                        <ListItemText primary={mode === "light" ? t.menuTheme.modeLight : t.menuTheme.modeDark} sx={listItemSx} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <p className="mtp-hint">
                            {t.menuTheme.modesHint}
                            <br />
                            {t.menuTheme.orderHint}
                        </p>
                    </div>

                    {/* A theme that draws one layout has nothing to choose between, so it says nothing. */}
                    {theme.layouts.length > 1 && (
                        <div className="mtp-field">
                            <FormControl fullWidth disabled={saving} sx={textFieldSx}>
                                <InputLabel>{t.menuTheme.layoutsLabel}</InputLabel>
                                <Select
                                    multiple
                                    value={layouts}
                                    onChange={(e) => handlePick(e.target.value as TyMenuLayout[], theme.layouts, setLayouts, layouts)}
                                    input={<OutlinedInput label={t.menuTheme.layoutsLabel} />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {(selected as TyMenuLayout[]).map((layout) => (
                                                <Chip
                                                    key={layout}
                                                    label={layout === "grid" ? t.menuTheme.layoutGrid : t.menuTheme.layoutList}
                                                    size="small"
                                                    sx={chipSx}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                    MenuProps={{ slotProps: { paper: { style: { maxHeight: 48 * 4.5 + 8 } } } }}
                                >
                                    {theme.layouts.map((layout) => (
                                        <MenuItem key={layout} value={layout}>
                                            <Checkbox checked={layouts.includes(layout)} sx={checkboxSx} />
                                            <ListItemText
                                                primary={layout === "grid" ? t.menuTheme.layoutGrid : t.menuTheme.layoutList}
                                                sx={listItemSx}
                                            />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <p className="mtp-hint">
                                {t.menuTheme.layoutsHint}
                                <br />
                                {t.menuTheme.orderHint}
                            </p>
                        </div>
                    )}

                    <div className="mtp-field">
                        <OutlinedField label={t.menuTheme.colorLabel}>
                            {theme.usesAccent ? (
                                <>
                                    <div className="mtp-color">
                                        {/* The native swatch is the picker; the field beside it is for pasting an exact brand hex. */}
                                        <input
                                            type="color"
                                            className="mtp-color-swatch"
                                            aria-label={t.menuTheme.colorLabel}
                                            value={isColorValid ? color : DEFAULT_MENU_COLOR}
                                            onChange={(e) => setColor(e.target.value.toUpperCase())}
                                            disabled={saving}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={color}
                                            onChange={(e) => setColor(e.target.value.toUpperCase())}
                                            error={!isColorValid}
                                            disabled={saving}
                                            slotProps={{ htmlInput: { dir: "ltr", maxLength: 7 } }}
                                            sx={textFieldSx}
                                        />
                                        <button
                                            type="button"
                                            className="mtp-color-reset"
                                            onClick={() => setColor(DEFAULT_MENU_COLOR)}
                                            disabled={saving || color === DEFAULT_MENU_COLOR}
                                        >
                                            {t.menuTheme.colorReset}
                                        </button>
                                    </div>
                                    <p className="mtp-hint" data-error={!isColorValid}>
                                        {isColorValid ? t.menuTheme.colorHint : t.menuTheme.colorInvalid}
                                    </p>
                                </>
                            ) : (
                                <p className="mtp-hint">{t.menuTheme.noColorHint}</p>
                            )}
                        </OutlinedField>
                    </div>
                </div>
            </div>

            <div className="mtp-footer">
                {saveError && <p className="mtp-error">{saveError}</p>}
                <button type="button" className="mtp-btn mtp-btn--cancel" onClick={onClose} disabled={saving}>
                    {t.menuTheme.cancelLabel}
                </button>
                <button
                    type="button"
                    className="mtp-btn mtp-btn--save"
                    onClick={handleApply}
                    disabled={saving || (theme.usesAccent && !isColorValid)}
                >
                    {saving ? t.menuTheme.savingLabel : isActive ? t.menuTheme.saveLabel : t.menuTheme.applyLabel}
                </button>
            </div>
        </div>
    );
}
